<template>
	<div ref="element" class="transition-height" :class="{ 'transition-height-active': isTweening }">
		<transition
			:duration="duration"
			:name="name"
			@before-leave="beforeLeave"
			@leave="leave"
			@after-leave="afterLeave"
			@before-enter="beforeEnter"
			@enter="enter"
			@after-enter="afterEnter"
		>
			<slot />
		</transition>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

// Refs
const element = ref<HTMLElement | null>(null)
let heightAnimation: Animation | null = null

// Props
const props = defineProps({
	// The name of Vue transition that should be used
	name: {
		type: String,
		required: true
	},

	// Same as Vue transition `mode` property
	mode: {
		type: String,
		default: '',
		validator(val: string) { return ['out-in', ''].includes(val); }
	},

	// Do a switch style tween, rather than a toggle (the default)
	switching: Boolean,

	// Same as Vue transition `duration` property
	duration: {
		type: Number,
		default: null
	}
})

// Emits
const emit = defineEmits(['beforeLeave', 'beforeEnter', 'leave', 'enter', 'afterLeave', 'afterEnter'])

// Reactives
const isTweening = ref(false)

// Methods
// Capture the intial height when switching or the starting height when
// toggling close
function beforeLeave(el: Element){
	if(element.value) {
		const height = stopHeightAnimation() ?? element.value.getBoundingClientRect().height
		element.value.style.height = height + 'px'
	}
	isTweening.value = true

	return emit('beforeLeave', el)
}

// Set the starting height for a toggle or preserve it when switching content.
function beforeEnter(el: Element){
	isTweening.value = true

	if(element.value) {
		const height = stopHeightAnimation() ?? element.value.getBoundingClientRect().height
		element.value.style.height = props.switching ? height + 'px' : '0px'
	}

	return emit('beforeEnter', el)
}

// When leaving, animate the wrapper closed unless this is a content switch.
function leave(el: Element){
	if(!props.switching && element.value) {
		animateHeight(element.value.getBoundingClientRect().height, 0, el)
	}

	return emit('leave', el)
}

function enter(el: Element){
	if(element.value?.style.height) {
		const startHeight = element.value.getBoundingClientRect().height
		animateHeight(startHeight, naturalHeight(), el)
	}

	return emit('enter', el);
}

// Reset the state unless we're doing an out-in transition, in which case
// this should get triggered on enter.
function afterLeave(el: Element){
	if(props.mode !== 'out-in' && !props.switching) {
		reset()
	}

	return emit('afterLeave', el);
}

// Clear the height after enter finishes in all cases. This is valid
// because either it's a toggle animation and this won't get called at all
// when v-if="false" or we're switching and it fires at the end in either
// supported case.
function afterEnter(el: Element){
	reset()

	return emit('afterEnter', el);
}

// Clear the inline height after Vue finishes, so the wrapper can size naturally.
function reset(){
	heightAnimation?.cancel()
	heightAnimation = null
	if(element.value) element.value.style.height = ''
	return isTweening.value = false
}

function stopHeightAnimation(): number | null {
	if(!heightAnimation || !element.value) return null

	// Preserve the in-flight height when a new transition interrupts this one.
	const height = element.value.getBoundingClientRect().height
	heightAnimation.cancel()
	heightAnimation = null
	return height
}

function animateHeight(from: number, to: number, transitionElement: Element): void {
	if(!element.value) return

	const { duration, delay } = transitionTiming(transitionElement)
	if(duration === 0) {
		element.value.style.height = to + 'px'
		return
	}

	heightAnimation?.cancel()
	heightAnimation = element.value.animate(
		[{ height: from + 'px' }, { height: to + 'px' }],
		// Keep the final animated height until Vue calls reset().
		{ duration, delay, easing: 'ease', fill: 'forwards' }
	)
}

function transitionTiming(element: Element): { duration: number; delay: number } {
	if(props.duration !== null) return { duration: props.duration, delay: 0 }

	// Match the longest CSS transition on the child so Vue waits for both effects.
	const styles = window.getComputedStyle(element)
	const durations = styles.transitionDuration.split(',').map(parseTime)
	const delays = styles.transitionDelay.split(',').map(parseTime)
	let duration = 0
	let delay = 0

	durations.forEach((item, index) => {
		const itemDelay = delays[index % delays.length] ?? 0
		if(item + itemDelay > duration + delay) {
			duration = item
			delay = itemDelay
		}
	})

	return { duration, delay }
}

function parseTime(value: string): number {
	const time = value.trim()
	const amount = parseFloat(time)
	return time.endsWith('ms') ? amount : amount * 1000
}

function naturalHeight(): number {
	if(!element.value) return 0

	// Read the wrapper's unconstrained height without leaving it expanded.
	const currentHeight = element.value.style.height
	element.value.style.height = 'auto'
	const height = element.value.getBoundingClientRect().height
	element.value.style.height = currentHeight

	return height
}
</script>